/**
 * Lógica do painel "Margem por Anúncio" — porte fiel do JS de
 * `scripts/margem_ml/gen_relatorio.py` (fiesta_party_dados) para TypeScript.
 *
 * REGRA QUE MANDA AQUI: este arquivo **não calcula margem**. A margem é
 * `calculated_margin`, vinda de `mongodb_financial.margens`, e quem confere a
 * fórmula pedido a pedido é o `montar_dados.py` do pipeline (RUNBOOK §5). O que
 * este arquivo faz é filtrar por período/loja/logística e SOMAR o que já veio
 * pronto. Nada é estimado, nada vem da API do Mercado Livre.
 *
 * A única exceção é o CMV informado à mão pela pessoa que usa o painel: quando
 * um pedido está com `cmv = 0` na base, ele fica FORA do cálculo (regra nº 2 da
 * skill `fiesta-base-dados`) até alguém digitar um custo — e aí a MC daquele
 * pedido é recalculada aqui, no navegador, sem tocar na base.
 */

/* ---------- formato do arquivo (data.json do pipeline) ------------------- */

/** Uma linha de `ads`: agregado por anúncio. Índices como montar_dados.py monta. */
export type RawAd = (string | number)[]

/** Uma linha de `det[ad]`: um pedido. */
export type RawDet = (string | number)[]

export type RawMargem = {
  ads: RawAd[]
  det: Record<string, RawDet[]>
  accs: string[]
  short: string[]
  accmk: number[]
  mknome?: string[]
  periodo?: { ini?: string; fim?: string; label?: string; gerado?: string }
  conf?: {
    pedido: { n: number; rev: number }
    venda: { n: number; rev: number }
    ad: { n: number; rev: number }
    ref?: { ml?: { rev?: number }; sh?: { n?: number } }
    lag?: string
  }
  /**
   * Margem ajustada v2 (06/10/2026): vem pronta de `fiestadados.margens_ajustadas`, lida pelo
   * serviço `services/margem-anuncios`. `calculada_em` = `ultima_rodada` do `_meta` (ISO UTC);
   * `marcas` = pedidos por marca no período extraído. Coleção vazia → `calculada_em: null` e
   * `aviso`. Ausente = payload antigo (arquivo estático anterior à v2): a tela não mostra
   * ajustada nenhuma além da oficial — nada é estimado aqui.
   */
  ajustada?: {
    calculada_em: string | null
    versao?: string | null
    marcas?: Record<string, number>
    aviso?: string
  }
  loja2?: { l1_margem?: number; l1_fonte?: number; l2_margem?: number; l2_fonte?: number }
  meta_bi?: { exportado_em?: string; encoding_origem?: string }
}

/* Índices do detalhe por pedido — nomeados para não errar coluna. */
export const D_PED = 0
export const D_DATA = 1
export const D_QTD = 2
export const D_RECEITA = 3
// Total já calculado na origem com o rebate de cada componente. Não descontar novamente.
export const D_CMV = 4
export const D_FEE = 5
export const D_SHP = 6
export const D_TAX = 7
export const D_DSC = 8
export const D_MC = 9
export const D_FLAGS = 10
export const D_PAGO = 11
export const D_REBATE = 12
export const D_CORR = 13
/**
 * 14–17 e 22–24 vêm da coleção `margens_ajustadas` (margem ajustada v2). `D_MCAJ` já inclui o
 * ajuste de PIS/COFINS e o do regime de MG — **não somar `D_AJPIS` de novo**.
 */
export const D_MCAJ = 14
/** 0/1: o pedido tem alguma marca (dado tratado por falta de informação). Não é mais "produto ST". */
export const D_TEM_MARCA = 15
export const D_DICMS = 16
export const D_DCMV = 17
export const D_LOG = 18
export const D_ICMS = 19
export const D_DIFAL = 20
/** PIS/COFINS como saiu na nota fiscal (parte de `taxes`). */
export const D_PIS = 21
/** ajuste de PIS/COFINS da coleção (já dentro de `D_MCAJ`) — só para a memória de cálculo. */
export const D_AJPIS = 22
/** MC oficial corrigida = oficial + DIFAL fantasma devolvido. */
export const D_MCOC = 23
/** marcas do pedido, separadas por ";". */
export const D_MARCAS = 24

/** Marca que o serviço põe no pedido sem documento na coleção (ajustada = oficial). */
export const SEM_AJUSTADA = 'sem_ajustada'

/** Marcas de um pedido (coluna 24). Payload antigo não tem a coluna → lista vazia. */
export const marcasDoPedido = (r: RawDet): string[] =>
  String(r[D_MARCAS] ?? '')
    .split(';')
    .map((m) => m.trim())
    .filter(Boolean)

/** O payload traz a margem ajustada v2? (o arquivo estático antigo não traz) */
export const temAjustadaV2 = (raw: RawMargem) => raw.ajustada !== undefined

export const AVISO_PAYLOAD_ANTIGO =
  'este arquivo é anterior à margem ajustada v2 (não traz a coleção margens_ajustadas) — a MC ajustada fica igual à oficial'

/** Descrição curta de cada marca (spec §6.4). Marca desconhecida aparece pelo próprio nome. */
export const ROTULO_MARCA: Record<string, string> = {
  sem_ajustada: 'pedido sem documento na coleção — ajustada = oficial',
  uf_desconhecida: 'UF do destino não encontrada — tratado como fora de MG',
  conta_desconhecida: 'conta não resolvida — sem regime',
  aliquota_nao_identificada: 'alíquota interna não identificada — sem ICMS economizado',
  origem_desconhecida: 'origem (nacional/importado) desconhecida — sem ICMS economizado',
  nao_classificado: 'SKU fora da classificação fiscal — sem crédito perdido',
  classificacao_parcial: 'kit com parte dos componentes classificados',
  st_confirmada_05_10: 'grupo sem ST (confirmação dela, 05/10/2026)',
  maquina_lote_st: 'máquina ainda do lote com ST — sem crédito perdido',
  sem_cmv_componente: 'pedido com CMV zero em margens (regra nº 2) — sem crédito perdido',
  rateio_incompleto: 'kit com componente sem custo em cmvs — rateio parcial',
  pedido_id_via_pack: 'pedido_id recuperado pelo pack_id (ETL de 31/08 sem pedido_id)',
  difal_fantasma: 'DIFAL de 6% em venda dentro de MG devolvido',
  difal_intra_mg_atipico: 'DIFAL dentro de MG fora do padrão — não devolvido',
  frete_negativo: 'frete gravado negativo — usado em módulo',
  duplicado_em_margens: 'mais de uma linha em margens — vale a mais recente',
  sem_pedido_id: 'linha de margens sem pedido_id',
  icms_ausente: 'ICMS em branco em margens — sem ICMS economizado',
}

/** ISO UTC → "dd/mm hh:mm" no horário de Brasília. */
export function horaBrasilia(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  )
  return `${p.day}/${p.month} ${p.hour}:${p.minute}`
}

/* Índices do agregado por anúncio que a tela usa (o resto é recalculado). */
export const A_AD = 0
export const A_TITULO = 1
export const A_SKU = 2
export const A_ACC = 3
export const A_MK = 17

/** Bits de `flags`, iguais aos de montar_dados.py. */
export const F_SEM_COMISSAO = 1
export const F_SEM_ENVIO = 2
export const F_SEM_CMV = 4
export const F_PAGO_DIFERENTE = 8
export const F_ENVIO_NEGATIVO = 16
export const F_FORMULA_NAO_FECHA = 32
export const F_REBATE_EMBUTIDO = 64
export const F_REPOSTO = 128
/** CMV maior que a receita: carga da `cmvs` quebrada (06/10/2026: `3,477` gravado como 3477). */
export const F_CMV_SUSPEITO = 256

/**
 * Pedido com CMV suspeito fica FORA do cálculo, como o `cmv = 0`. A tela decide
 * sozinha (CMV > receita) além de honrar o bit do serviço, para se proteger
 * mesmo quando o `montar.py` em produção ainda não marca.
 */
export const cmvSuspeito = (r: RawDet) =>
  !!(Number(r[D_FLAGS]) & F_CMV_SUSPEITO) ||
  (Number(r[D_RECEITA]) > 0 && Number(r[D_CMV]) > Number(r[D_RECEITA]))

/* ---------- formatação (mesmos formatos do artifact) --------------------- */

export const brl = (n: number) =>
  n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const brl0 = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
export const pct = (n: number) =>
  `${(n * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
/** "2026-09-10 14:32" -> "10/09 14:32" */
export const dataBr = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11)}`

const MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
export const dataLabel = (s: string) => `${s.slice(8, 10)} ${MES[+s.slice(5, 7) - 1]}`

/* ---------- metadados e views ------------------------------------------- */

export type MetaAd = { t: string; sk: string; acc: number; mk: number }

export function metaPorAnuncio(raw: RawMargem): Record<string, MetaAd> {
  const meta: Record<string, MetaAd> = {}
  for (const a of raw.ads) {
    meta[String(a[A_AD])] = {
      t: String(a[A_TITULO] ?? ''),
      sk: String(a[A_SKU] ?? ''),
      acc: Number(a[A_ACC] ?? 0),
      mk: Number(a[A_MK] ?? 0),
    }
  }
  return meta
}

/** Dias com pedido, ordenados. Define o alcance do seletor de período. */
export function diasDisponiveis(raw: RawMargem): string[] {
  const s = new Set<string>()
  for (const linhas of Object.values(raw.det)) {
    for (const r of linhas) s.add(String(r[D_DATA]).slice(0, 10))
  }
  return [...s].sort()
}

/**
 * View: -1 = consolidado · -2-mk = um canal inteiro · 0..n = uma loja.
 * Canal com uma loja só não ganha aba própria (seria a mesma coisa duas vezes).
 */
export const viewDoCanal = (mk: number) => -2 - mk
export const mkDaView = (v: number) => (v <= -2 ? -2 - v : -1)
export const naView = (a: { acc: number; mk: number }, v: number) =>
  v === -1 ? true : v <= -2 ? a.mk === mkDaView(v) : a.acc === v

export const lojasDoCanal = (raw: RawMargem, mk: number) =>
  raw.accmk.map((_, i) => i).filter((i) => raw.accmk[i] === mk)

/**
 * Nomes dos canais, um por índice de `accmk`.
 *
 * ⚠️ Não use `raw.mknome` direto com fallback de tamanho fixo. O `data.json` de
 * 11/09/2026 foi gerado por uma versão do `montar_dados.py` que ainda não
 * gravava `mknome`, e o fallback de dois canais do artifact
 * (`D.mknome||['Mercado Livre','Shopee']`) faz **Shein, Amazon e TikTok
 * sumirem das abas e do bloco de canais** — 7,27% da receita, que continuava
 * aparecendo só no consolidado. A quantidade de canais é sempre `accmk`; o
 * nome é o que o arquivo trouxer. Canal sem nome e com uma loja só herda o nome
 * da loja; com mais de uma, fica um rótulo genérico — nunca um palpite de marca.
 */
export function nomesDosCanais(raw: RawMargem): string[] {
  const qtd = raw.accmk.length ? Math.max(...raw.accmk) + 1 : 0
  return Array.from({ length: qtd }, (_, mk) => {
    const doArquivo = raw.mknome?.[mk]
    if (doArquivo) return doArquivo
    const lojas = lojasDoCanal(raw, mk)
    return lojas.length === 1 ? raw.accs[lojas[0]] : `Canal ${mk}`
  })
}

export function montarTabs(raw: RawMargem): Array<[string, number]> {
  const nomes = nomesDosCanais(raw)
  const tabs: Array<[string, number]> = [['Consolidado', -1]]
  nomes.forEach((nome, mk) => {
    const lojas = lojasDoCanal(raw, mk)
    if (!lojas.length) return
    if (lojas.length > 1) {
      tabs.push([nome, viewDoCanal(mk)])
      lojas.forEach((i) => tabs.push([raw.accs[i], i]))
    } else {
      tabs.push([raw.accs[lojas[0]], lojas[0]])
    }
  })
  return tabs
}

/* ---------- margem ajustada ---------------------------------------------- */

/*
 * Desde a margem ajustada v2 (06/10/2026) a tela NÃO calcula PIS/COFINS efetivo nem o regime de
 * MG: as parcelas vêm prontas da coleção `margens_ajustadas` (regra na skill
 * `margem-ajustada-fiesta`, serviço `services/margem-ajustada`). Recalcular aqui somava o
 * PIS/COFINS duas vezes, porque `D_MCAJ` já o inclui.
 */

/* ---------- agregação por anúncio (o `montarA` do artifact) -------------- */

export type Filtro = {
  d1: string
  d2: string
  /** 'all' | 'F' (Full) | 'N' (envio normal) | '?' (não identificado) */
  log: string
}

/** CMV por unidade informado à mão, por anúncio. */
export type CmvManual = Record<string, number>

export type LinhaAnuncio = {
  ad: string
  t: string
  sk: string
  acc: number
  mk: number
  ped: number
  un: number
  rev: number
  paid: number
  cmv: number
  fee: number
  shp: number
  tax: number
  icms: number
  difal: number
  pis: number
  dsc: number
  reb: number
  mg: number
  /** pedidos com alguma marca (coluna `D_TEM_MARCA`) */
  nMarca: number
  /** contagens de pedidos por tipo de pendência */
  n1: number
  n2: number
  n3: number
  n4: number
  n5: number
  n6: number
  /** pedidos com CMV maior que a receita (carga da `cmvs` quebrada) — fora do cálculo */
  n7: number
  npend: number
  /** pedidos e receita que ficaram FORA do cálculo por não ter CMV ou por CMV suspeito */
  exPed: number
  exRev: number
  /** pedidos recalculados com CMV informado à mão */
  manPed: number
  temMan: boolean
  pct: number
  avg: number
  /* --- margem ajustada (pronta da coleção margens_ajustadas) --- */
  /** PIS/COFINS que a NF cobrou (parte de `tax`, já dentro da MC oficial) */
  pisNota: number
  /** ajuste de PIS/COFINS da coleção (coluna 22) — já dentro de `mgAjustada` */
  ajPis: number
  /** ICMS economizado no regime de MG (Σ dif_icms) */
  difIcms: number
  /** crédito perdido na transferência (Σ dif_cmv) — custo */
  difCmv: number
  /** DIFAL fantasma devolvido (Σ mc_oficial_corrigida − oficial) */
  difalFantasma: number
  /** ajustada − oficial atual − parcelas: `margens` mudou depois da rodada da coleção */
  residuo: number
  /** MC ajustada = Σ mc_ajustada da coleção (pedido sem documento: = oficial) */
  mgAjustada: number
  pctAjustada: number
  /** pedidos por marca, no recorte */
  marcas: Record<string, number>
}

const dentroDoPeriodo = (r: RawDet, f: Filtro) => {
  const d = String(r[D_DATA]).slice(0, 10)
  return d >= f.d1 && d <= f.d2
}
const logOk = (r: RawDet, f: Filtro) => f.log === 'all' || r[D_LOG] === f.log
/** Filtro completo de uma linha de pedido: período + tipo de envio. */
export const linhaVale = (r: RawDet, f: Filtro) => dentroDoPeriodo(r, f) && logOk(r, f)

/** Marca do serviço para o DIFAL de 6% devolvido em venda dentro de MG. */
export const MARCA_DIFAL_FANTASMA = 'difal_fantasma'

/**
 * Parcelas da margem ajustada de um pedido, como vieram da coleção. Payload antigo (sem
 * `raw.ajustada`) → ajustada = oficial e parcelas zero: a coluna 14 do arquivo antigo é a regra
 * do incentivo que saiu, e a tela não estima nada no lugar dela.
 *
 * `difalFantasma` só existe no pedido com a marca `difal_fantasma`. Qualquer outra diferença
 * entre a ajustada da coleção e a oficial ATUAL de `margens` (que pode ter sido reprocessada
 * depois da rodada da coleção) vai para `residuo` — linha própria na tela, nunca escondida
 * dentro de uma parcela fiscal. Fecha sempre:
 *   mcaj = mc + difalFantasma + ajPis + difIcms − difCmv + residuo
 */
export function ajustadaDoPedido(r: RawDet, v2: boolean) {
  const mc = Number(r[D_MC])
  if (!v2) {
    return { mcaj: mc, ajPis: 0, difIcms: 0, difCmv: 0, difalFantasma: 0, residuo: 0, marcas: [] as string[] }
  }
  const mcoc = r[D_MCOC] !== undefined && r[D_MCOC] !== '' ? Number(r[D_MCOC]) : mc
  const marcas = marcasDoPedido(r)
  const mcaj = r[D_MCAJ] !== undefined ? Number(r[D_MCAJ]) : mc
  const ajPis = Number(r[D_AJPIS] || 0)
  const difIcms = Number(r[D_DICMS] || 0)
  const difCmv = Number(r[D_DCMV] || 0)
  const difalFantasma = marcas.includes(MARCA_DIFAL_FANTASMA) ? mcoc - mc : 0
  const residuo = mcaj - mc - (difalFantasma + ajPis + difIcms - difCmv)
  return { mcaj, ajPis, difIcms, difCmv, difalFantasma, residuo, marcas }
}


/**
 * Agrega os pedidos por anúncio dentro do filtro. Espelho de `montarA()`.
 *
 * Pedido com `cmv = 0` fica fora do cálculo (conta em `exPed`/`exRev`) até
 * existir CMV informado para aquele anúncio — aí entra com o custo digitado,
 * descontado igual da oficial e da ajustada (sem crédito estimado).
 */
export function agregarAnuncios(
  raw: RawMargem,
  filtro: Filtro,
  cmvManual: CmvManual,
): LinhaAnuncio[] {
  const meta = metaPorAnuncio(raw)
  const v2 = temAjustadaV2(raw)
  const out: LinhaAnuncio[] = []

  for (const ad of Object.keys(raw.det)) {
    const linhas = raw.det[ad].filter((r) => linhaVale(r, filtro))
    if (!linhas.length) continue
    const m = meta[ad] || { t: '', sk: '', acc: 0, mk: 0 }
    const man = cmvManual[ad]

    let un = 0, rev = 0, paid = 0, cmv = 0, fee = 0, shp = 0, tax = 0
    let icms = 0, difal = 0, pis = 0, dsc = 0, reb = 0, mg = 0, mgAjustada = 0
    let nMarca = 0, n1 = 0, n2 = 0, n3 = 0, n4 = 0, n5 = 0, n6 = 0, n7 = 0, ped = 0
    let exPed = 0, exRev = 0, manPed = 0
    let ajPis = 0, difIcms = 0, difCmv = 0, difalFantasma = 0, residuo = 0
    const marcas: Record<string, number> = {}

    for (const r of linhas) {
      const flags = Number(r[D_FLAGS])
      if (flags & F_SEM_COMISSAO) n1++
      if (flags & F_SEM_ENVIO) n2++
      if (flags & F_SEM_CMV) n3++
      if (flags & F_ENVIO_NEGATIVO) n4++
      if (flags & F_FORMULA_NAO_FECHA) n5++
      if (flags & F_REPOSTO) n6++

      if (cmvSuspeito(r)) {
        // #63: custo inflado por carga errada — fora do cálculo
        n7++
        exPed++
        exRev += Number(r[D_RECEITA])
        continue
      }

      const aj = ajustadaDoPedido(r, v2)
      let c = Number(r[D_CMV])
      let g = Number(r[D_MC])
      let gj = aj.mcaj

      if (flags & F_SEM_CMV) {
        if (man == null) {
          exPed++
          exRev += Number(r[D_RECEITA])
          continue // regra nº 2: sem CMV, fora do cálculo
        }
        c = man * Number(r[D_QTD])
        g = Number(r[D_MC]) - c
        gj = gj - c // mesmo custo nas duas margens; nenhum crédito estimado na tela
        manPed++
      }

      ped++
      un += Number(r[D_QTD])
      rev += Number(r[D_RECEITA])
      paid += Number(r[D_PAGO])
      cmv += c
      fee += Number(r[D_FEE])
      shp += Number(r[D_SHP])
      tax += Number(r[D_TAX])
      icms += Number(r[D_ICMS] || 0)
      difal += Number(r[D_DIFAL] || 0)
      pis += Number(r[D_PIS] || 0)
      dsc += Number(r[D_DSC])
      reb += Number(r[D_REBATE])
      mg += g
      mgAjustada += gj
      ajPis += aj.ajPis
      difIcms += aj.difIcms
      difCmv += aj.difCmv
      difalFantasma += aj.difalFantasma
      residuo += aj.residuo
      if (v2 && r[D_TEM_MARCA]) nMarca++
      for (const k of aj.marcas) marcas[k] = (marcas[k] || 0) + 1
    }

    out.push({
      ad, t: m.t, sk: m.sk, acc: m.acc, mk: m.mk,
      ped, un, rev, paid, cmv, fee, shp, tax, icms, difal, pis, dsc, reb, mg, nMarca,
      n1, n2, n3, n4, n5, n6, n7, npend: n1 + n2 + n3 + n4 + n5 + n7,
      exPed, exRev, manPed, temMan: man != null,
      pct: rev ? mg / rev : 0,
      avg: ped ? mg / ped : 0,
      pisNota: pis, ajPis, difIcms, difCmv, difalFantasma, residuo,
      mgAjustada,
      pctAjustada: rev ? mgAjustada / rev : 0,
      marcas,
    })
  }

  out.sort((x, y) => y.rev - x.rev)
  return out
}

export const naViewLinha = (a: LinhaAnuncio, v: number) => naView(a, v)
export const somar = <K extends keyof LinhaAnuncio>(s: LinhaAnuncio[], k: K) =>
  s.reduce((x, a) => x + Number(a[k]), 0)

/* ---------- blocos do painel -------------------------------------------- */

export type LinhaLadoALado = {
  view: number
  label: string
  sub: string
  grupo: boolean
  rev: number
  mg: number
  ped: number
  pct: number
  participacao: number
}

/** "Canais e lojas, lado a lado" — hierarquia canal → loja. */
export function ladoALado(raw: RawMargem, todos: LinhaAnuncio[]): LinhaLadoALado[] {
  const nomes = nomesDosCanais(raw)
  const defs: Array<[number, string, string]> = [
    [-1, 'Consolidado', `${raw.accs.length} lojas em ${nomes.length} canais`],
  ]
  nomes.forEach((nome, mk) => {
    const lojas = lojasDoCanal(raw, mk)
    if (!lojas.length) return
    if (lojas.length > 1) {
      defs.push([viewDoCanal(mk), nome, `${lojas.length} lojas`])
      lojas.forEach((i) => defs.push([i, raw.accs[i], nome]))
    } else {
      defs.push([lojas[0], raw.accs[lojas[0]], nome])
    }
  })
  const total = somar(todos, 'rev')
  return defs.map(([v, label, sub]) => {
    const s = todos.filter((a) => naView(a, v))
    const rev = somar(s, 'rev')
    const mg = somar(s, 'mg')
    const ped = somar(s, 'ped')
    return {
      view: v, label, sub, grupo: v < 0, rev, mg, ped,
      pct: rev ? mg / rev : 0,
      participacao: total ? rev / total : 0,
    }
  })
}

export type LinhaComposicao = {
  nome: string
  descricao: string
  valor: number
  pctReceita: number
  credito?: boolean
  total?: boolean
}

/** "Composição do custo — campo a campo". Textos iguais aos do artifact. */
export function composicaoCusto(s: LinhaAnuncio[]): LinhaComposicao[] {
  const rev = somar(s, 'rev')
  const defs: Array<[string, string, keyof LinhaAnuncio]> = [
    ['CMV', 'cmv', 'cmv'],
    ['Impostos', 'taxes — soma dos três abaixo, das notas fiscais', 'tax'],
    ['↳ ICMS', 'icms — imposto da saída', 'icms'],
    ['↳ DIFAL', 'difal — diferencial de alíquota das vendas interestaduais', 'difal'],
    ['↳ PIS/COFINS', 'pis_cofins', 'pis'],
    [
      'Comissão do marketplace',
      'marketplace_fees — no ML é taxa percentual + taxa fixa − estornos; na Shopee é o escrow_fee, que já embute o frete. A Amazon não grava esse campo — ver o alerta de comissão zerada.',
      'fee',
    ],
    [
      'Envio do vendedor',
      'shipping_cost — o Mercado Livre, a Amazon e o TikTok gravam esse campo separado; a Shopee e a Shein não.',
      'shp',
    ],
    [
      'Descontos',
      'discount (ML, Amazon, TikTok) + seller_discount (Shopee, Shein)',
      'dsc',
    ],
  ]
  const linhas: LinhaComposicao[] = defs
    .filter(([, , k]) => somar(s, k) !== 0)
    .map(([nome, descricao, k]) => ({
      nome,
      descricao,
      valor: somar(s, k),
      pctReceita: rev ? somar(s, k) / rev : 0,
    }))

  const reb = somar(s, 'reb')
  if (reb) {
    linhas.push({
      nome: 'Reembolso da Shopee',
      descricao:
        'rebate — entra somando na margem. Conta só os pedidos em que a base usa esse modelo.',
      valor: reb,
      pctReceita: rev ? reb / rev : 0,
      credito: true,
    })
  }
  const mg = somar(s, 'mg')
  linhas.push({
    nome: 'Margem de contribuição',
    descricao:
      'calculated_margin — receita menos os custos, mais o reembolso. Confere ao centavo em todos os pedidos, menos os sinalizados no bloco de alerta.',
    valor: mg,
    pctReceita: rev ? mg / rev : 0,
    total: true,
  })
  return linhas
}

export type LinhaPendencia = {
  descricao: string
  pedidos: number
  base: number
  pctBase: number
  legenda: string
}

/** "Pedidos com campo de custo zerado". A base do % muda por linha (ML x todos). */
export function pendencias(s: LinhaAnuncio[]): { linhas: LinhaPendencia[]; total: number; pedidos: number } {
  const ped = somar(s, 'ped')
  const pedML = s.filter((a) => a.mk === 0).reduce((x, a) => x + a.ped, 0)
  const defs: Array<[string, keyof LinhaAnuncio, number, string]> = [
    ['marketplace_fees = 0 — comissão não lançada', 'n1', ped, 'dos pedidos'],
    ['shipping_cost = 0 — envio não lançado (só Mercado Livre; a Shopee não usa esse campo)', 'n2', pedML, 'dos pedidos do ML'],
    ['cmv = 0 — custo da mercadoria não cadastrado', 'n3', ped, 'dos pedidos'],
    ['shipping_cost gravado negativo', 'n4', pedML, 'dos pedidos do ML'],
    ['a fórmula da MC não fecha — calculated_margin não bate com os campos do próprio registro', 'n5', ped, 'dos pedidos'],
    ['cmv maior que a receita — carga da cmvs quebrada (custo ×1000); fora do cálculo', 'n7', ped, 'dos pedidos'],
  ]
  return {
    linhas: defs
      .filter(([, , base]) => base > 0)
      .map(([descricao, k, base, legenda]) => ({
        descricao,
        pedidos: somar(s, k),
        base,
        pctBase: base ? somar(s, k) / base : 0,
        legenda,
      })),
    total: somar(s, 'npend'),
    pedidos: ped,
  }
}

export type FaixaMC = { label: string; cor: string; n: number; rev: number; pctRev: number }

/** Distribuição dos anúncios por faixa de MC. Mesmos cortes do artifact. */
export function faixasMC(s: LinhaAnuncio[]): FaixaMC[] {
  const defs: Array<[string, (a: LinhaAnuncio) => boolean, string]> = [
    ['< 0%', (a) => a.pct < 0, 'bad'],
    ['0–15%', (a) => a.pct >= 0 && a.pct < 0.15, 'b1'],
    ['15–25%', (a) => a.pct >= 0.15 && a.pct < 0.25, 'b2'],
    ['25–35%', (a) => a.pct >= 0.25 && a.pct < 0.35, 'b3'],
    ['≥ 35%', (a) => a.pct >= 0.35, 'b4'],
  ]
  const total = somar(s, 'rev')
  return defs.map(([label, f, cor]) => {
    const g = s.filter(f)
    const rev = somar(g, 'rev')
    return { label, cor, n: g.length, rev, pctRev: total ? rev / total : 0 }
  })
}

export type Kpis = ReturnType<typeof kpis>

/** Os 10 indicadores do topo. */
export function kpis(s: LinhaAnuncio[]) {
  const rev = somar(s, 'rev')
  const mg = somar(s, 'mg')
  const ped = somar(s, 'ped')
  const un = somar(s, 'un')
  const neg = s.filter((a) => a.mg < 0 && a.ped > 0)
  const exPed = somar(s, 'exPed')
  const exRev = somar(s, 'exRev')
  const manPed = somar(s, 'manPed')
  const semCmv = s.filter((a) => a.exPed > 0)
  const limpos = s.filter((a) => a.npend === 0 && a.ped > 0)
  const paid = somar(s, 'paid')
  return {
    rev, mg, ped, un, paid,
    pct: rev ? mg / rev : 0,
    mcPorPedido: ped ? mg / ped : 0,
    ticket: ped ? rev / ped : 0,
    receitaDaLoja: rev - somar(s, 'dsc'),
    /** > 1 quando o `paid_value` é escrow (Shopee), não receita */
    gapPago: rev - paid,
    custoMarketplace: somar(s, 'fee') + somar(s, 'shp'),
    fee: somar(s, 'fee'),
    shp: somar(s, 'shp'),
    anunciosNoVermelho: neg.length,
    receitaNoVermelho: somar(neg, 'rev'),
    anunciosLimpos: limpos.length,
    anuncios: s.length,
    exPed, exRev, anunciosSemCmv: semCmv.length, manPed,
  }
}

export type Banner = ReturnType<typeof bannerEnvio>

/**
 * Banner do ML: quanto pedido está sem envio lançado e qual seria a MC só nos
 * pedidos completos. Conta PEDIDO a PEDIDO, não por anúncio.
 */
export function bannerEnvio(
  raw: RawMargem,
  filtro: Filtro,
  view: number,
  cmvManual: CmvManual,
) {
  const meta = metaPorAnuncio(raw)
  let pedML = 0, semEnv = 0, revEnv = 0, negEnv = 0, corrEnv = 0, negCorr = 0
  let pedOk = 0, revOk = 0, mgOk = 0, revML = 0, mgML = 0
  let revSH = 0, paidSH = 0, dscSH = 0, rebSH = 0, mgSH = 0, feeSH = 0

  for (const ad of Object.keys(raw.det)) {
    const m = meta[ad]
    if (!m || !naView(m, view)) continue
    const man = cmvManual[ad]
    for (const r of raw.det[ad].filter((x) => linhaVale(x, filtro))) {
      const flags = Number(r[D_FLAGS])
      if (m.mk === 1) {
        rebSH += Number(r[D_REBATE]); mgSH += Number(r[D_MC]); feeSH += Number(r[D_FEE])
      }
      if (cmvSuspeito(r)) continue
      if (flags & F_SEM_CMV && man == null) continue
      const g = flags & F_SEM_CMV && man != null
        ? Number(r[D_MC]) - man * Number(r[D_QTD])
        : Number(r[D_MC])
      if (m.mk === 1) {
        revSH += Number(r[D_RECEITA]); paidSH += Number(r[D_PAGO]); dscSH += Number(r[D_DSC])
        continue
      }
      if (m.mk !== 0) continue
      pedML++; revML += Number(r[D_RECEITA]); mgML += g
      if (flags & F_SEM_ENVIO) { semEnv++; revEnv += Number(r[D_RECEITA]) }
      if (flags & F_ENVIO_NEGATIVO) {
        negEnv++
        if (Number(r[D_CORR] || 0) > 0) { negCorr++; corrEnv += Number(r[D_CORR]) }
      }
      // "completo" = comissão e envio gravados, envio não negativo e fórmula fechando.
      // Envio negativo entrava como completo e INFLAVA a MC dos completos (13/10/2026).
      if ((flags & F_INCOMPLETO) === 0) { pedOk++; revOk += Number(r[D_RECEITA]); mgOk += g }
    }
  }
  return {
    mostrarML: semEnv > 0 || negEnv > 0,
    pedML, semEnv, revEnv, negEnv, corrEnv, negCorr,
    pedOk, revOk,
    mcCompletos: revOk ? mgOk / revOk : 0,
    mcML: revML ? mgML / revML : 0,
    mostrarShopee: revSH > 0,
    revSH, paidSH, dscSH, rebSH, mgSH, feeSH,
    gapSH: revSH - paidSH,
    /** o que sobra da comissão depois de descontar o gap bruto × pago */
    feeLiquidoSH: feeSH - (revSH - paidSH),
    receitaLojaSH: revSH - dscSH,
  }
}

export type ContagemLogistica = { chave: string; rotulo: string; pedidos: number; receita: number }

/**
 * Contagem por tipo de envio do ML (`shippings.logistic_type`).
 * O campo parou de ser gravado em jul/2026 e voltou em set — por isso existe o
 * grupo "não identificado", que nunca é escondido nem chutado.
 */
export function contagemLogistica(
  raw: RawMargem,
  filtro: Filtro,
  view: number,
): { linhas: ContagemLogistica[]; pctNaoIdentificado: number; total: number } {
  const meta = metaPorAnuncio(raw)
  const c: Record<string, [number, number]> = { F: [0, 0], N: [0, 0], '?': [0, 0] }
  // a contagem ignora o próprio filtro de logística (senão o botão zeraria os outros)
  const semLog: Filtro = { ...filtro, log: 'all' }
  for (const ad of Object.keys(raw.det)) {
    const m = meta[ad]
    if (!m || m.mk !== 0 || !naView(m, view)) continue
    for (const r of raw.det[ad].filter((x) => linhaVale(x, semLog))) {
      const k = r[D_LOG] === 'F' || r[D_LOG] === 'N' ? String(r[D_LOG]) : '?'
      c[k][0]++
      c[k][1] += Number(r[D_RECEITA])
    }
  }
  const total = c.F[0] + c.N[0] + c['?'][0]
  const linhas: ContagemLogistica[] = [
    { chave: 'all', rotulo: 'Todos', pedidos: total, receita: c.F[1] + c.N[1] + c['?'][1] },
    { chave: 'F', rotulo: 'Full', pedidos: c.F[0], receita: c.F[1] },
    { chave: 'N', rotulo: 'Envio normal', pedidos: c.N[0], receita: c.N[1] },
    { chave: '?', rotulo: 'Não identificado', pedidos: c['?'][0], receita: c['?'][1] },
  ]
  return { linhas, pctNaoIdentificado: total ? c['?'][0] / total : 0, total }
}

/** A view tem algum anúncio do Mercado Livre? (o filtro de envio só faz sentido aí) */
export function viewTemML(raw: RawMargem, view: number): boolean {
  const meta = metaPorAnuncio(raw)
  return Object.keys(raw.det).some((ad) => {
    const m = meta[ad]
    return !!m && m.mk === 0 && naView(m, view)
  })
}

export type AjusteRegime = {
  /** lojas do recorte que têm parcela do regime (na prática, a Extrema); "" se nenhuma */
  conta: string
  rev: number
  pedidos: number
  mg: number
  mgaj: number
  /** ICMS economizado (Σ dif_icms) */
  difIcms: number
  /** crédito perdido na transferência (Σ dif_cmv) — entra como custo */
  difCmv: number
  /** DIFAL fantasma devolvido (Σ mc_oficial_corrigida − oficial) */
  difalFantasma: number
  /** ajustada − oficial atual − parcelas: `margens` mudou depois da rodada da coleção */
  residuo: number
  /** ajuste de PIS/COFINS (Σ coluna 22) — já dentro de `mgaj` */
  ajPis: number
  /** ISO UTC da última rodada do serviço da coleção; null = coleção vazia ou payload antigo */
  calculadaEm: string | null
  versao: string | null
  /** pedidos por marca, no recorte */
  marcas: Record<string, number>
  pedidosComMarca: number
  /** coleção vazia ou payload antigo — a tela mostra em destaque */
  aviso?: string
  view: number
}

/**
 * Bloco do regime de e-commerce de MG (substitui o antigo `incentivoFiscal`). Só SOMA as
 * parcelas que a coleção `margens_ajustadas` já calculou, pedido a pedido, para os anúncios da
 * view (`linhas`) dentro do filtro. Nada é estimado; parâmetro nenhum mora na tela.
 *
 *   MC ajustada = oficial + DIFAL fantasma devolvido + ajuste de PIS/COFINS
 *                 + ICMS economizado − crédito perdido na transferência
 *                 + resíduo (`margens` mudou depois da rodada da coleção)
 * `conta` = lojas com ICMS economizado ou crédito perdido (o regime de fato), não as do resíduo.
 */
export function ajusteRegime(
  raw: RawMargem,
  filtro: Filtro,
  view: number,
  cmvManual: CmvManual,
  linhas: LinhaAnuncio[],
): AjusteRegime | null {
  if (!linhas.length) return null
  const v2 = temAjustadaV2(raw)
  const ads = new Set(linhas.map((a) => a.ad))
  const meta = metaPorAnuncio(raw)
  let rev = 0, mg = 0, mgaj = 0, difIcms = 0, difCmv = 0, difalFantasma = 0, ajPis = 0, residuo = 0
  let pedidos = 0, pedidosComMarca = 0
  const marcas: Record<string, number> = {}
  const accsComRegime = new Set<number>()

  for (const ad of Object.keys(raw.det)) {
    if (!ads.has(ad)) continue
    const man = cmvManual[ad]
    for (const r of raw.det[ad]) {
      if (!linhaVale(r, filtro)) continue
      if (cmvSuspeito(r)) continue
      const semCmv = !!(Number(r[D_FLAGS]) & F_SEM_CMV)
      if (semCmv && man == null) continue // fora do cálculo, como no resto do painel
      const c = semCmv && man != null ? man * Number(r[D_QTD]) : 0
      const aj = ajustadaDoPedido(r, v2)
      pedidos++
      rev += Number(r[D_RECEITA])
      mg += Number(r[D_MC]) - c
      mgaj += aj.mcaj - c
      difIcms += aj.difIcms
      difCmv += aj.difCmv
      difalFantasma += aj.difalFantasma
      residuo += aj.residuo
      ajPis += aj.ajPis
      if (aj.marcas.length) pedidosComMarca++
      for (const k of aj.marcas) marcas[k] = (marcas[k] || 0) + 1
      if (aj.difIcms || aj.difCmv) accsComRegime.add(meta[ad]?.acc ?? -1)
    }
  }

  const aj = raw.ajustada
  const out: AjusteRegime = {
    conta: [...accsComRegime].filter((i) => i >= 0).sort((x, y) => x - y).map((i) => raw.accs[i]).join(' + '),
    rev, pedidos, mg, mgaj, difIcms, difCmv, difalFantasma, residuo, ajPis,
    calculadaEm: aj?.calculada_em ?? null,
    versao: aj?.versao ?? null,
    marcas, pedidosComMarca, view,
  }
  if (!v2) out.aviso = AVISO_PAYLOAD_ANTIGO
  else if (aj?.aviso) out.aviso = aj.aviso
  return out
}

/* ---------- tabela ------------------------------------------------------- */

export type FiltroTabela = '' | 'neg' | 'lo' | 'pend' | 'limpo' | 'cmv0'

export function filtrarTabela(
  s: LinhaAnuncio[],
  busca: string,
  filtro: FiltroTabela,
  sortK: keyof LinhaAnuncio,
  sortD: number,
): LinhaAnuncio[] {
  const q = busca.trim().toLowerCase()
  let r = s
  if (q) r = r.filter((a) => `${a.ad} ${a.t} ${a.sk}`.toLowerCase().includes(q))
  if (filtro === 'neg') r = r.filter((a) => a.mg < 0 && a.ped > 0)
  if (filtro === 'lo') r = r.filter((a) => a.pct < 0.15)
  if (filtro === 'pend') r = r.filter((a) => a.npend > 0)
  if (filtro === 'limpo') r = r.filter((a) => a.npend === 0)
  if (filtro === 'cmv0') r = r.filter((a) => a.exPed > 0)
  return r.slice().sort((x, y) => {
    const a = x[sortK]
    const b = y[sortK]
    return typeof a === 'string'
      ? sortD * a.localeCompare(String(b))
      : sortD * (Number(a) - Number(b))
  })
}

export type LinhaDetalhe = {
  raw: RawDet
  pedido: string
  data: string
  qtd: number
  receita: number
  pago: number
  cmv: number
  fee: number
  shp: number
  tax: number
  icms: number
  difal: number
  pis: number
  dsc: number
  reb: number
  corr: number
  mc: number
  pct: number
  /* --- MC ajustada do pedido (pronta da coleção margens_ajustadas; ela, 15/10/2026: "preciso ver no detalhe linha a linha") --- */
  /** MC ajustada = mc_ajustada da coleção; com CMV informado à mão, o mesmo custo é descontado (sem crédito estimado) */
  mcaj: number
  pctaj: number
  /** parcelas, só para a memória de cálculo (já dentro de `mcaj`) */
  ajPis: number
  difIcms: number
  difCmv: number
  difalFantasma: number
  residuo: number
  marcas: string[]
  /** ficou fora do cálculo por não ter CMV e ninguém ter informado */
  fora: boolean
  cmvInformado: boolean
  tags: string[]
}

const TAGS: Array<[number, string]> = [
  [F_SEM_COMISSAO, 'sem comissão'],
  [F_SEM_ENVIO, 'sem envio'],
  [F_FORMULA_NAO_FECHA, 'fórmula não fecha'],
  [F_PAGO_DIFERENTE, 'pago ≠ receita'],
  [F_REBATE_EMBUTIDO, 'rebate embutido na receita'],
  [F_REPOSTO, 'reposto'],
]

/**
 * Bits que tiram o pedido da base "completos" (a que serve para decidir preço):
 * comissão ou envio não lançados, envio negativo e fórmula que não fecha.
 */
export const F_INCOMPLETO = F_SEM_COMISSAO | F_SEM_ENVIO | F_ENVIO_NEGATIVO | F_FORMULA_NAO_FECHA

/** Chips do detalhe. Envio negativo só é "corrigido" quando a fórmula confirmou (D_CORR > 0). */
export function tagsDoPedido(flags: number, corr: number): string[] {
  const tags = TAGS.filter(([b]) => flags & b).map(([, n]) => n)
  if (flags & F_ENVIO_NEGATIVO) {
    tags.splice(2, 0, corr > 0 ? 'envio corrigido' : 'envio negativo · dado suspeito')
  }
  return tags
}

/** Detalhe pedido a pedido de um anúncio, mais recentes primeiro. */
export function detalhe(
  raw: RawMargem,
  ad: string,
  filtro: Filtro,
  cmvManual: CmvManual,
): { linhas: LinhaDetalhe[]; totais: Omit<LinhaDetalhe, 'raw' | 'pedido' | 'data' | 'tags' | 'fora' | 'cmvInformado'>; fora: number } {
  const man = cmvManual[ad]
  const v2 = temAjustadaV2(raw)
  const todos = (raw.det[ad] || []).filter((r) => linhaVale(r, filtro))
  const linhas: LinhaDetalhe[] = todos.map((r) => {
    const flags = Number(r[D_FLAGS])
    const suspeito = cmvSuspeito(r)
    const semCmv = !suspeito && !!(flags & F_SEM_CMV)
    const fora = suspeito || (semCmv && man == null)
    const cmv = semCmv && man != null ? man * Number(r[D_QTD]) : Number(r[D_CMV])
    const mc = semCmv && man != null ? Number(r[D_MC]) - cmv : Number(r[D_MC])
    const aj = ajustadaDoPedido(r, v2)
    // espelho de agregarAnuncios(): CMV informado à mão desconta igual nas duas margens
    const mcaj = semCmv && man != null ? aj.mcaj - cmv : aj.mcaj
    const receita = Number(r[D_RECEITA])
    const tags = tagsDoPedido(flags, Number(r[D_CORR] || 0))
    if (suspeito) tags.unshift('fora do cálculo · CMV suspeito (maior que a receita)')
    else if (fora) tags.unshift('fora do cálculo · sem CMV')
    else if (semCmv) tags.unshift('CMV informado')
    return {
      raw: r,
      pedido: String(r[D_PED]),
      data: String(r[D_DATA]),
      qtd: Number(r[D_QTD]),
      receita,
      pago: Number(r[D_PAGO]),
      cmv,
      fee: Number(r[D_FEE]),
      shp: Number(r[D_SHP]),
      tax: Number(r[D_TAX]),
      icms: Number(r[D_ICMS] || 0),
      difal: Number(r[D_DIFAL] || 0),
      pis: Number(r[D_PIS] || 0),
      dsc: Number(r[D_DSC]),
      reb: Number(r[D_REBATE]),
      corr: Number(r[D_CORR] || 0),
      mc,
      pct: receita ? mc / receita : 0,
      mcaj,
      pctaj: receita ? mcaj / receita : 0,
      ajPis: aj.ajPis,
      difIcms: aj.difIcms,
      difCmv: aj.difCmv,
      difalFantasma: aj.difalFantasma,
      residuo: aj.residuo,
      marcas: aj.marcas,
      fora,
      cmvInformado: semCmv && man != null,
      tags,
    }
  })

  const val = linhas.filter((l) => !l.fora)
  const s = (k: keyof LinhaDetalhe) => val.reduce((x, l) => x + Number(l[k]), 0)
  const receita = s('receita')
  const mc = s('mc')
  const mcaj = s('mcaj')
  return {
    linhas,
    fora: linhas.length - val.length,
    totais: {
      qtd: s('qtd'), receita, pago: s('pago'), cmv: s('cmv'), fee: s('fee'),
      shp: s('shp'), tax: s('tax'), icms: s('icms'), difal: s('difal'), pis: s('pis'),
      dsc: s('dsc'), reb: s('reb'), corr: s('corr'), mc,
      pct: receita ? mc / receita : 0,
      mcaj,
      pctaj: receita ? mcaj / receita : 0,
      ajPis: s('ajPis'), difIcms: s('difIcms'), difCmv: s('difCmv'),
      difalFantasma: s('difalFantasma'), residuo: s('residuo'),
      marcas: [],
    },
  }
}

/* ---------- botões rápidos de período ------------------------------------ */

const NOME_MES: Record<string, string> = {
  '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
  '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
  '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro',
}

/** "Tudo / últimos 7 / últimos 15" + um botão por mês (ou quinzenas, se for um mês só). */
export function botoesPeriodo(dias: string[]): Array<{ label: string; d1: string; d2: string }> {
  if (!dias.length) return []
  const dmin = dias[0]
  const dmax = dias[dias.length - 1]
  const ult = (n: number) => dias[Math.max(0, dias.length - n)]
  const meses: Record<string, string[]> = {}
  dias.forEach((d) => {
    const k = d.slice(0, 7)
    ;(meses[k] = meses[k] || []).push(d)
  })
  const btns = [
    { label: 'Tudo', d1: dmin, d2: dmax },
    { label: 'Últimos 7 dias', d1: ult(7), d2: dmax },
    { label: 'Últimos 15 dias', d1: ult(15), d2: dmax },
  ]
  const chaves = Object.keys(meses).sort()
  if (chaves.length > 1) {
    chaves.forEach((k) => {
      const g = meses[k]
      btns.push({ label: NOME_MES[k.slice(5, 7)], d1: g[0], d2: g[g.length - 1] })
    })
  } else {
    btns.push(
      { label: '1ª quinzena', d1: dmin, d2: `${dmin.slice(0, 8)}15` },
      { label: '2ª quinzena', d1: `${dmin.slice(0, 8)}16`, d2: dmax },
    )
  }
  return btns
}

/** Classe de cor pela MC %: negativa, abaixo de 15%, ou saudável. */
export const classeMC = (p: number) => (p < 0 ? 'neg' : p < 0.15 ? 'mid' : 'pos')
